import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import DynamicForm from "../../components/ui/dynamicForm/dynamicForm.component";
import { useCRUD } from "../../api/crud";
import { USED_PARTS_ENDPOINT, JOB_TASKS_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { ApiFieldConfigMap, DynamicFieldsConfig } from "../../types/dynamicForm";

export const UsedPartDetailsActions = () => {
  const { id, usedPartId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const usedPartIdToUse = usedPartId || id;
  const { loading, error, remove, get } = useCRUD(USED_PARTS_ENDPOINT);
  // A part sent by courier belongs to its shipment: it goes back to stock with
  // the shipment, never by editing or deleting the row here.
  const [issuedForShipment, setIssuedForShipment] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      if (!usedPartIdToUse) return;
      const response = await get(usedPartIdToUse);
      setIssuedForShipment(Boolean(response?.data?.shipment_info));
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedPartIdToUse]);
  const [updateUsedPartOpened, setUpdateUsedPartOpened] = useState<boolean>(false);
  const [deleteUsedPartOpened, setDeleteUsedPartOpened] = useState<boolean>(false);
  const canChangeUsedPart = useHasPermission("service.change_usedpart");
  const canDeleteUsedPart = useHasPermission("service.delete_usedpart");

  const handleDeleteUsedPart = async () => {
    if (loading || !usedPartIdToUse) {
      return;
    }

    const result = await remove({ id: usedPartIdToUse });

    if (result?.success) {
      enqueueSnackbar(t('Used part deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteUsedPartOpened(false);
      if (id) {
        navigate(`/service/stock-status/${id}/used-parts`);
      } else {
        navigate('/service/stock-status');
      }
    } else {
      enqueueSnackbar(t('Failed to delete used part'), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = issuedForShipment ? [] : [
    {
      icon: <EditIcon />,
      label: t("Update used part"),
      onClick: () => {
        setUpdateUsedPartOpened(true);
      },
      disabled: !canChangeUsedPart,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete used part"),
      onClick: () => {
        setDeleteUsedPartOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteUsedPart,
    },
  ];

  const apiFields: ApiFieldConfigMap = {
    // `part`/`ordered_part` aren't editable here - which OrderedPart batch(es)
    // back a used part is decided automatically (FIFO) at creation time and
    // isn't reassignable afterwards; only quantity/job_task can be updated.
    job_task: { url: JOB_TASKS_ENDPOINT, field: "display_name" },
  };

  const dynamicFields: DynamicFieldsConfig = {};

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this used part will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteUsedPart}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateUsedPartOpened}
        setOpened={setUpdateUsedPartOpened}
        title={t("Update used part")}
        content={
          <DynamicForm
            url={USED_PARTS_ENDPOINT}
            id={usedPartIdToUse}
            onSuccess={() => {
              setUpdateUsedPartOpened(false);
              window.dispatchEvent(new Event("usedPartUpdated"));
              window.dispatchEvent(new Event("partUpdated"));
            }}
            apiFields={apiFields}
            dynamicFields={dynamicFields}
          />
        }
      />
      <CustomModal
        opened={deleteUsedPartOpened}
        setOpened={setDeleteUsedPartOpened}
        title={t("Delete used part")}
        content={deleteModalContent}
      />
    </>
  );
};
